/**
 * The world clock. Presentational time only: rendering reads it for sun angle
 * and sky, but nothing in `src/world` may — baseline content stays a pure
 * function of position and seed, whatever the hour.
 *
 * Time moves only through `advance(dtReal)`; the clock never reads the wall
 * clock itself, so a paused frame loop pauses the world.
 *
 * ## There is one speed, and it is not adjustable
 *
 * The hud used to carry four buttons — still, slow, day, years — and law 27
 * has always said they were dev scaffolding that retires with the server:
 * **THE SHARED WORLD HAS ONE CLOCK.** They are gone. A world where the player
 * can stop time is not the world `DESIGN.md` §1.1 describes ("not a static
 * sandbox that waits for the player"), and leaving a pause button on screen
 * taught exactly the wrong thing about what kind of game this is.
 *
 * ## Why 192 and not D §4's 10,000
 *
 * `DESIGN.md` §4 wants ~5,000 world years inside a six-month Age, which is
 * 10,000x, and `ROADMAP.md` §17.1 recorded that as canon with one gate on it:
 * *an 8.64-second day may be more than the renderer or the screenshot gate can
 * survive — measure it before changing this.* That measurement now exists
 * (`tools/daynight.ts`) and it says:
 *
 *     speed     a day    a bright spell   the midday gate
 *      192     7.5 min       211 s             9.0 s
 *   10,000     8.6 s          4.1 s            173 ms
 *
 * It does not strobe — 518 frames a day at 60 fps, 0.69° of sun per frame.
 * What it does is make **the sky the fastest thing in the world**: a bright
 * spell of four seconds is not long enough to cross a valley, and the working
 * screenshot gate stops being holdable by hand.
 *
 * The two goals turn out to be separable. D §4 asks for 10,000x so that *five
 * thousand years* fit in six months — a claim about **history**, not about
 * light — and nothing in it says the sun has to keep pace with the calendar.
 * Until that separation is designed, this stays at the speed the world is
 * actually legible at, and the choice is one constant rather than a UI.
 */

/** World-seconds in one day. */
export const DAY = 86400;

/** Days in a world year — the calendar the Chronicle and the hud count in. */
export const YEAR_DAYS = 365;

/**
 * The one speed. DESIGN-V §1: one world day in 7.5 real minutes. Changing
 * this is a design decision, not a setting, and `tools/daynight.ts` prices it.
 */
export const CANON_SPEED = 192;

export class WorldClock {
  /** World-seconds elapsed. Starts mid-morning on day 0 — light, not darkness. */
  t = 0.30 * DAY;

  /**
   * World-seconds per real second. Writable only so a headless probe can wind
   * a world forward faster than play; nothing in the running game sets it, and
   * there is no longer any control that can.
   */
  speed = CANON_SPEED;

  /** Advance by real elapsed seconds, scaled to world time. */
  advance(dtReal: number): void {
    this.t += dtReal * this.speed;
  }

  /** Fraction of the current day: 0 = midnight, 0.5 = noon. */
  get tod(): number {
    return (this.t % DAY) / DAY;
  }

  /** Whole days elapsed since the world began. */
  get day(): number {
    return Math.floor(this.t / DAY);
  }

  /** Whole years elapsed. */
  get year(): number {
    return Math.floor(this.day / YEAR_DAYS);
  }

  /** Day within the current year, 1-based, as a calendar would count it. */
  get dayOfYear(): number {
    return (this.day % YEAR_DAYS) + 1;
  }

  /** "14:26". */
  get timeText(): string {
    const s = this.t % DAY;
    const hh = String(Math.floor(s / 3600)).padStart(2, '0');
    const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  /**
   * What the hud shows: the year first, because at 192x a year is thirty-six
   * real hours and it is the unit a god actually lives in — the day and the
   * hour are what the *people* live in, and they come after.
   */
  get clockText(): string {
    return `year ${this.year} · day ${this.dayOfYear} · ${this.timeText}`;
  }
}
